import { useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * `window.confirm()` is unreliable inside sandboxed iframes — Chrome blocks
 * cross-origin modals from iframes without the `allow-modals` sandbox flag,
 * which is why "Delete vehicle" appeared broken in the Emergent preview
 * while working on the production top-level domain.
 *
 * This hook is a drop-in imperative replacement:
 *
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   // …
 *   const handleDelete = async (id) => {
 *     const ok = await confirm({
 *       title: "Delete vehicle?",
 *       description: "This will also remove every trip and energy event…",
 *       confirmLabel: "Delete",
 *       destructive: true,
 *     });
 *     if (!ok) return;
 *     …
 *   };
 *   // In JSX (mount ONCE per page):
 *   <ConfirmDialog />
 */
export default function useConfirm() {
  const [state, setState] = useState({
    open: false,
    title: "Are you sure?",
    description: "",
    confirmLabel: "Confirm",
    cancelLabel: "Cancel",
    destructive: false,
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState((s) => ({
        ...s,
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        destructive: !!opts.destructive,
      }));
    });
  }, []);

  const respond = useCallback((answer) => {
    setState((s) => ({ ...s, open: false }));
    if (resolverRef.current) {
      resolverRef.current(answer);
      resolverRef.current = null;
    }
  }, []);

  const ConfirmDialog = useCallback(
    () => (
      <AlertDialog
        open={state.open}
        onOpenChange={(o) => {
          if (!o) respond(false);
        }}
      >
        <AlertDialogContent
          className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] sm:max-w-[420px]"
          data-testid="confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="text-lg tracking-tight"
              data-testid="confirm-dialog-title"
            >
              {state.title}
            </AlertDialogTitle>
            {state.description && (
              <AlertDialogDescription
                className="text-[13px] text-[var(--text-secondary)] leading-relaxed"
                data-testid="confirm-dialog-description"
              >
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel
              onClick={() => respond(false)}
              className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
              data-testid="confirm-dialog-cancel"
            >
              {state.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => respond(true)}
              className={
                state.destructive
                  ? "bg-[#E82127] hover:bg-[#C81C22] text-white"
                  : "bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:opacity-90"
              }
              data-testid="confirm-dialog-confirm"
            >
              {state.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    ),
    [state, respond]
  );

  return { confirm, ConfirmDialog };
}
